import React, { useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const PhotoUploadZone = ({
  uploadedFile,
  setUploadedFile,
  uploadProgress,
  setUploadProgress,
  isUploading,
  setIsUploading,
  errors,
  setErrors,
  onFileSelect,
  onVideoModeSelect, // Video upload moduna geçmek için
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);

  // Mode: photo | video
  const [mode, setMode] = useState("photo");

  /* -------------------------------
     IMAGE VALIDATION
  -------------------------------- */
  const validateFile = (file) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 10 * 1024 * 1024;

    if (!file || !validTypes.includes(file.type))
      return "Please upload a JPG or PNG image file";
    if (file.size > maxSize)
      return "File size must be less than 10MB";

    return null;
  };

  /* -------------------------------
     IMAGE FILE SELECT
  -------------------------------- */
  const handleFileSelect = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrors((prev) => ({ ...prev, file: error }));
      return;
    }

    setErrors((prev) => ({ ...prev, file: null }));
    setIsUploading(true);
    setUploadProgress(0);

    const uploadInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setIsUploading(false);
          setUploadedFile(file);

          const reader = new FileReader();
          reader.onload = (e) => setPreview(e.target.result);
          reader.readAsDataURL(file);

          if (onFileSelect) onFileSelect(file);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  };

  /* -------------------------------
     DRAG EVENTS
  -------------------------------- */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0])
      handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files?.[0])
      handleFileSelect(e.target.files[0]);
  };

  /* -------------------------------
     REMOVE IMAGE
  -------------------------------- */
  const removeFile = () => {
    setUploadedFile(null);
    setPreview(null);
    setUploadProgress(0);
    setErrors((prev) => ({ ...prev, file: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = (bytes / Math.pow(k, i)).toFixed(2);
    return `${size} ${["Bytes", "KB", "MB"][i]}`;
  };

  /* ======================================================
     UI
  ======================================================= */
  return (
    <div className="space-y-6">
      {/* MODE SWITCH */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setMode("photo")}
          className={`
            px-4 py-2 rounded-lg text-sm font-medium transition-all
            ${
              mode === "photo"
                ? "bg-cyan-500 text-white shadow-lg shadow-cyan-500/30"
                : "bg-white/10 text-white/60 hover:bg-white/20"
            }
          `}
        >
          Upload Photo
        </button>

        <button
          onClick={() => setMode("video")}
          className={`
            px-4 py-2 rounded-lg text-sm font-medium transition-all
            ${
              mode === "video"
                ? "bg-cyan-500 text-white shadow-lg shadow-cyan-500/30"
                : "bg-white/10 text-white/60 hover:bg-white/20"
            }
          `}
        >
          Upload Video
        </button>
      </div>

      {/* ======================================================
         MODE: VIDEO (UPLOAD)
      ======================================================= */}
      {mode === "video" && (
        <div
          className="
            rounded-xl p-8 border border-white/10 bg-white/5 text-center
            flex flex-col items-center space-y-5 shadow-inner
          "
        >
          <Icon name="Video" size={38} color="white" className="opacity-80" />

          <h3 className="text-lg font-semibold text-white">
            Video-Based Graft Analysis
          </h3>

          <p className="text-sm text-slate-300 max-w-md leading-relaxed">
            Upload a recorded scalp video from your device.  
            The AI will analyze graft distribution across frames for
            enhanced accuracy.
          </p>

          <Button
            onClick={onVideoModeSelect}
            variant="outline"
            size="lg"
            className="
              border-white/20 text-white hover:border-cyan-400
              hover:bg-white/10 mt-4
            "
          >
            <Icon name="Video" size={18} className="mr-2" />
            Upload & Analyze Video
          </Button>
        </div>
      )}

      {/* ======================================================
         MODE: PHOTO (EXISTING FLOW)
      ======================================================= */}
      {mode === "photo" && (
        <>
          {!uploadedFile ? (
            <div
              className={`
                relative rounded-xl p-8 text-center transition-all duration-300 border-2 border-dashed
                ${
                  dragActive
                    ? "border-cyan-400 bg-white/10 scale-[1.02]"
                    : "border-white/20 bg-white/5 hover:border-cyan-300 hover:bg-white/10"
                }
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              style={{ minHeight: "240px" }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileInputChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploading}
              />

              <div className="flex flex-col items-center justify-center space-y-4">
                <div
                  className={`
                    w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300
                    ${
                      dragActive
                        ? "bg-cyan-400/20 scale-110"
                        : "bg-white/10"
                    }
                  `}
                >
                  <Icon
                    name="Upload"
                    size={28}
                    color={dragActive ? "rgb(34 211 238)" : "white"}
                  />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-white">
                    {dragActive
                      ? "Drop your image here"
                      : "Upload Patient Photo"}
                  </h3>
                  <p className="text-sm text-slate-300 max-w-md leading-relaxed">
                    Use high-quality scalp photos under{" "}
                    <span className="font-medium text-white">
                      medical lighting
                    </span>
                    . Avoid shadows, blur, and noise.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    disabled={isUploading}
                    className="border-white/20 text-white hover:border-cyan-400 hover:bg-white/10"
                  >
                    <Icon name="FolderOpen" size={18} className="mr-2" />
                    Choose File
                  </Button>
                  <div className="text-xs text-slate-400">
                    JPG, PNG • Max 2MB • Min 1024×768px
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* PREVIEW CARD */
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-24 h-24 bg-white/10 rounded-lg border border-white/20 overflow-hidden flex-shrink-0 shadow-md">
                  <img
                    src={preview}
                    alt="Upload preview"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-semibold text-white truncate">
                        {uploadedFile?.name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-slate-300">
                        <span className="flex items-center">
                          <Icon
                            name="HardDrive"
                            size={14}
                            className="mr-1"
                          />
                          {formatFileSize(uploadedFile?.size)}
                        </span>
                        <span className="flex items-center">
                          <Icon name="Image" size={14} className="mr-1" />
                          {uploadedFile?.type
                            ?.split("/")?.[1]
                            ?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeFile}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10 -mt-1"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>

                  <div className="flex items-center space-x-2 bg-white/10 px-3 py-2 rounded-lg border border-white/20">
                    <Icon
                      name="CheckCircle"
                      size={16}
                      color="rgb(34 197 94)"
                    />
                    <span className="text-sm font-medium text-green-300">
                      Upload Successful
                    </span>
                    <span className="text-xs text-slate-300">
                      • Ready for analysis
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* UPLOAD PROGRESS */}
          {isUploading && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center">
                  <Icon
                    name="Upload"
                    size={16}
                    color="white"
                    className="animate-bounce"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-white">
                      Uploading image...
                    </span>
                    <span className="text-cyan-300">
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className="bg-cyan-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ERROR */}
          {errors?.file && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <Icon
                  name="AlertCircle"
                  size={16}
                  color="rgb(248 113 113)"
                />
                <div>
                  <p className="text-sm font-medium text-red-300">
                    Upload Error
                  </p>
                  <p className="text-sm text-red-400">
                    {errors?.file}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PhotoUploadZone;
